import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { debugLog } from "../services/debugLog";

const ZOOM_MAX = 12;
/** Absolute floor if viewport/board not measurable yet. */
const ZOOM_MIN_FLOOR = 0.05;
/** Ignore ResizeObserver blips below this — MAX sheet animation often reports height:0. */
const MIN_VIEWPORT_PX = 48;

export type MapViewportHandle = {
  fit: () => void;
  getTransform: () => { zoom: number; panX: number; panY: number; vw: number; vh: number };
  /** Width-fit zoom (board width = viewport width). */
  getFitZoom: () => number;
  /** Zoom toward viewport center (working-area magnification). */
  setZoomCentered: (zoom: number) => void;
  /**
   * Pan so a board point is centered. `targetZoom` optional — defaults to current zoom.
   */
  focusPoint: (boardX: number, boardY: number, targetZoom?: number) => void;
};

export type MapTransformInfo = {
  zoom: number;
  panX: number;
  panY: number;
  vw: number;
  vh: number;
};

type Props = {
  children: React.ReactNode;
  className?: string;
  boardWidth: number;
  boardHeight: number;
  /** Re-fit when board size or SVG content changes */
  fitKey?: string;
  onTransform?: (info: MapTransformInfo) => void;
  /** Fill parent via inline absolute inset (wins over Tailwind position utilities). */
  fill?: boolean;
  /**
   * Initial / fit vertical alignment.
   * Visitor app: `center` so the plan isn't glued under the search bar.
   * Admin editor: keep `top` (default).
   */
  fitAlignY?: "top" | "center";
  /** Reserved px at top (e.g. floating search) when computing fit pan. */
  fitInsetTop?: number;
  /** Reserved px at bottom (e.g. tab bar) when computing fit pan. */
  fitInsetBottom?: number;
  /**
   * Min zoom as a fraction of width-fit (1 = cannot zoom out past «fit width»).
   * Admin map editor uses ~0.25 so the whole pavilion + margins fit on screen.
   */
  minZoomRatio?: number;
};

/**
 * Iron rules:
 * 1) Viewport fills its parent (working area) by width + height.
 * 2) Min zoom = width-fit — cannot zoom out so the map is narrower than the screen.
 */
export const MapViewport = forwardRef<MapViewportHandle, Props>(function MapViewport(
  {
    children,
    className = "",
    boardWidth,
    boardHeight,
    fitKey = "",
    onTransform,
    fill = false,
    fitAlignY = "top",
    fitInsetTop = 0,
    fitInsetBottom = 0,
    minZoomRatio = 1,
  },
  ref,
) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef({ w: boardWidth, h: boardHeight });
  boardRef.current = { w: boardWidth, h: boardHeight };
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const lastGoodSize = useRef({ w: 0, h: 0 });
  const minZoomRef = useRef(ZOOM_MIN_FLOOR);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const userInteractedRef = useRef(false);
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null);
  const panDrag = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    panning: boolean;
  } | null>(null);
  const onTransformRef = useRef(onTransform);
  onTransformRef.current = onTransform;

  const emit = useCallback((z: number, p: { x: number; y: number }, vw: number, vh: number) => {
    onTransformRef.current?.({
      zoom: z,
      panX: p.x,
      panY: p.y,
      vw,
      vh,
    });
  }, []);

  const readSize = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return { width: 0, height: 0 };
    const rect = viewport.getBoundingClientRect();
    const width = rect.width || viewport.clientWidth || 0;
    const height = rect.height || viewport.clientHeight || 0;
    return { width, height };
  }, []);

  /** Zoom where board width equals viewport width (comfortable “fit width”). */
  const widthFitZoom = useCallback((vw: number, bw: number) => {
    if (vw < MIN_VIEWPORT_PX || bw <= 0) return ZOOM_MIN_FLOOR;
    return vw / bw;
  }, []);

  const clampZoom = useCallback(
    (z: number, vw?: number) => {
      const width = vw ?? lastGoodSize.current.w ?? readSize().width;
      const bw = boardRef.current.w;
      const fit = widthFitZoom(width, bw);
      const ratio = Math.min(1, Math.max(0.1, minZoomRatio));
      // Default floor = width-fit. Admin may lower ratio to zoom out and see margins.
      const minZ = Math.max(ZOOM_MIN_FLOOR, fit * ratio);
      minZoomRef.current = minZ;
      return Math.min(ZOOM_MAX, Math.max(minZ, Number(z.toFixed(4))));
    },
    [minZoomRatio, readSize, widthFitZoom],
  );

  const sync = useCallback(
    (z: number, p: { x: number; y: number }, vw?: number, vh?: number) => {
      const width = vw ?? lastGoodSize.current.w;
      const clamped = clampZoom(z, width);
      zoomRef.current = clamped;
      panRef.current = p;
      setZoom(clamped);
      setPan(p);
      const viewport = viewportRef.current;
      const rect = viewport?.getBoundingClientRect();
      emit(
        clamped,
        p,
        width || rect?.width || lastGoodSize.current.w,
        vh ?? rect?.height ?? lastGoodSize.current.h,
      );
    },
    [clampZoom, emit],
  );

  const fit = useCallback(() => {
    const { w: bw, h: bh } = boardRef.current;
    if (bw <= 0 || bh <= 0) return;
    const { width, height } = readSize();
    if (width < MIN_VIEWPORT_PX || height < MIN_VIEWPORT_PX) {
      debugLog.warn("map", "MapViewport.fit skipped (viewport too small)", {
        width,
        height,
        bw,
        bh,
        keptZoom: zoomRef.current,
        lastGood: lastGoodSize.current,
      });
      return;
    }
    lastGoodSize.current = { w: width, h: height };
    // Width edges = screen; vertical: top (admin) or center in usable band (visitor).
    const z = clampZoom(widthFitZoom(width, bw), width);
    const insetTop = Math.max(0, fitInsetTop);
    const insetBottom = Math.max(0, fitInsetBottom);
    const usableH = Math.max(1, height - insetTop - insetBottom);
    const boardScreenH = bh * z;
    const y =
      fitAlignY === "center"
        ? insetTop + (usableH - boardScreenH) / 2
        : insetTop;
    const next = {
      x: (width - bw * z) / 2,
      y,
    };
    debugLog.info("map", "MapViewport.fit", {
      mode: fitAlignY === "center" ? "width-fit-center-y" : "width-edges-top",
      vw: Math.round(width),
      vh: Math.round(height),
      bw,
      bh,
      zoom: z,
      minZoom: minZoomRef.current,
      panX: Math.round(next.x),
      panY: Math.round(next.y),
      boardScreenH: Math.round(boardScreenH),
      insetTop,
      insetBottom,
    });
    sync(z, next, width, height);
  }, [clampZoom, fitAlignY, fitInsetBottom, fitInsetTop, readSize, sync, widthFitZoom]);

  /** After user pinch/pan: only re-clamp min zoom on resize, never reset to fit. */
  const reclampAfterResize = useCallback(() => {
    const { width, height } = readSize();
    if (width < MIN_VIEWPORT_PX || height < MIN_VIEWPORT_PX) return;
    lastGoodSize.current = { w: width, h: height };
    sync(zoomRef.current, panRef.current, width, height);
  }, [readSize, sync]);

  const focusPoint = useCallback(
    (boardX: number, boardY: number, targetZoom?: number) => {
      userInteractedRef.current = true;
      const { width, height } = readSize();
      if (width < MIN_VIEWPORT_PX || height < MIN_VIEWPORT_PX) return;
      lastGoodSize.current = { w: width, h: height };
      // Default: keep current zoom (only pan to the point). Pass targetZoom to zoom in.
      const z = clampZoom(
        targetZoom != null ? targetZoom : zoomRef.current,
        width,
      );
      const next = {
        x: width / 2 - boardX * z,
        y: height / 2 - boardY * z,
      };
      sync(z, next, width, height);
    },
    [clampZoom, readSize, sync, widthFitZoom],
  );

  const zoomAt = useCallback(
    (nextZoom: number, clientX: number, clientY: number) => {
      userInteractedRef.current = true;
      const viewport = viewportRef.current;
      if (!viewport) {
        sync(nextZoom, panRef.current);
        return;
      }
      const rect = viewport.getBoundingClientRect();
      const vw = rect.width || lastGoodSize.current.w;
      const vh = rect.height || lastGoodSize.current.h;
      if (vw < MIN_VIEWPORT_PX || vh < MIN_VIEWPORT_PX) {
        sync(nextZoom, panRef.current, vw, vh);
        return;
      }
      const mx = clientX - rect.left;
      const my = clientY - rect.top;
      const prev = zoomRef.current || 1;
      const z = clampZoom(nextZoom, vw);
      const scale = z / prev;
      const p = panRef.current;
      sync(
        z,
        {
          x: mx - (mx - p.x) * scale,
          y: my - (my - p.y) * scale,
        },
        vw,
        vh,
      );
    },
    [clampZoom, sync],
  );

  const getFitZoom = useCallback(() => {
    const { width } = readSize();
    const w = width || lastGoodSize.current.w;
    return widthFitZoom(w, boardRef.current.w);
  }, [readSize, widthFitZoom]);

  const setZoomCentered = useCallback(
    (nextZoom: number) => {
      const viewport = viewportRef.current;
      if (!viewport) {
        sync(nextZoom, panRef.current);
        return;
      }
      const rect = viewport.getBoundingClientRect();
      zoomAt(nextZoom, rect.left + rect.width / 2, rect.top + rect.height / 2);
    },
    [sync, zoomAt],
  );

  useImperativeHandle(
    ref,
    () => ({
      fit,
      focusPoint,
      getFitZoom,
      setZoomCentered,
      getTransform: () => {
        const { width, height } = readSize();
        return {
          zoom: zoomRef.current,
          panX: panRef.current.x,
          panY: panRef.current.y,
          vw: width || lastGoodSize.current.w,
          vh: height || lastGoodSize.current.h,
        };
      },
    }),
    [fit, focusPoint, getFitZoom, setZoomCentered, readSize],
  );

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    userInteractedRef.current = false;
    let raf2 = 0;
    let roTimer = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => fit());
    });
    // One short retry for WebView layout — not a cascade of reset timers.
    const bootTimer = window.setTimeout(fit, 120);
    // Resize from chrome (toolbars, side panels) must NOT re-fit — that jerks
    // the view when adding/deleting markers. Only reclamp pan/zoom bounds.
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height ?? 0;
      const w = entries[0]?.contentRect.width ?? 0;
      if (w < MIN_VIEWPORT_PX || h < MIN_VIEWPORT_PX) return;
      window.clearTimeout(roTimer);
      roTimer = window.setTimeout(() => {
        reclampAfterResize();
      }, 80);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro.disconnect();
      window.clearTimeout(roTimer);
      window.clearTimeout(bootTimer);
    };
  }, [fit, fitKey, reclampAfterResize]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.9 : 1.1;
      zoomAt(zoomRef.current * factor, e.clientX, e.clientY);
    };

    const touchDist = (a: Touch, b: Touch) =>
      Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        panDrag.current = null;
        const t0 = e.touches[0]!;
        const t1 = e.touches[1]!;
        pinchRef.current = {
          dist: touchDist(t0, t1) || 1,
          zoom: zoomRef.current,
        };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        const t0 = e.touches[0]!;
        const t1 = e.touches[1]!;
        const dist = touchDist(t0, t1) || 1;
        const next = pinchRef.current.zoom * (dist / pinchRef.current.dist);
        zoomAt(next, (t0.clientX + t1.clientX) / 2, (t0.clientY + t1.clientY) / 2);
      }
    };

    const onTouchEnd = () => {
      pinchRef.current = null;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [zoomAt]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    // Do NOT preventDefault on touchstart — that kills click on booth pins.
    // Start panning only after the finger moves past a small threshold.
    const PAN_THRESHOLD_PX = 8;
    let drag: {
      x: number;
      y: number;
      ox: number;
      oy: number;
      panning: boolean;
    } | null = null;

    const start = (e: TouchEvent) => {
      if (e.touches.length !== 1 || pinchRef.current) return;
      // Only skip when an editor pin claims the gesture (data-map-no-pan).
      if ((e.target as HTMLElement).closest("[data-map-no-pan]")) return;
      const t = e.touches[0]!;
      drag = {
        x: t.clientX,
        y: t.clientY,
        ox: panRef.current.x,
        oy: panRef.current.y,
        panning: false,
      };
    };
    const move = (e: TouchEvent) => {
      if (!drag || e.touches.length !== 1) return;
      const t = e.touches[0]!;
      const dx = t.clientX - drag.x;
      const dy = t.clientY - drag.y;
      if (!drag.panning) {
        if (Math.hypot(dx, dy) < PAN_THRESHOLD_PX) return;
        drag.panning = true;
      }
      // Claim only once it's clearly a pan (keeps taps clickable).
      e.preventDefault();
      userInteractedRef.current = true;
      sync(zoomRef.current, {
        x: drag.ox + dx,
        y: drag.oy + dy,
      });
    };
    const end = () => {
      drag = null;
    };

    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchmove", move, { passive: false });
    el.addEventListener("touchend", end);
    el.addEventListener("touchcancel", end);
    return () => {
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", end);
      el.removeEventListener("touchcancel", end);
    };
  }, [sync]);

  const onPointerDown = (e: React.PointerEvent) => {
    // Any click/drag on the board counts as interaction — never auto-fit after that.
    userInteractedRef.current = true;
    if (e.pointerType === "touch") return;
    if ((e.target as HTMLElement).closest("[data-map-no-pan]")) return;
    panDrag.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: panRef.current.x,
      origY: panRef.current.y,
      panning: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = panDrag.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.panning) {
      if (Math.hypot(dx, dy) < 8) return;
      drag.panning = true;
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    }
    userInteractedRef.current = true;
    sync(zoomRef.current, {
      x: drag.origX + dx,
      y: drag.origY + dy,
    });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (panDrag.current?.pointerId === e.pointerId) panDrag.current = null;
  };

  return (
    <div
      ref={viewportRef}
      data-max-map-pan=""
      className={`overflow-hidden touch-none bg-slate-200 relative ${className}`}
      style={
        fill
          ? {
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              minHeight: "100%",
              boxSizing: "border-box",
              // Isolate from MAX/iOS edge-swipe-to-close while panning.
              touchAction: "none",
              overscrollBehavior: "none",
            }
          : {
              touchAction: "none",
              overscrollBehavior: "none",
            }
      }
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={fit}
    >
      <div
        className="absolute top-0 left-0"
        style={{
          width: boardWidth,
          height: boardHeight,
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: "0 0",
          // Board pixel size is fixed; zoom clamped in clampZoom (min = width-fit).
          minWidth: boardWidth,
          minHeight: boardHeight,
        }}
      >
        {children}
      </div>
    </div>
  );
});
